/**
 * Class for SliderSwipers
 */
export class SliderSwipers {
	static setupModules() {
		const moduleEls = document.querySelectorAll('.wp-block-qte-theme-child-slider');

		for (const moduleEl of moduleEls) {
			const module = new SliderSwipers(moduleEl);
			moduleEl.qteSwiperModule = module;
			module.init();
		}
	}

	constructor(moduleEl) {
		this.moduleEl = moduleEl;
		this.config = null;
		this.usePaginationProgress = moduleEl.dataset?.swiperProgress === '1';
	}

	async init() {
		try {
			const [{ default: Swiper }] = await Promise.all([import('swiper/bundle')]);
			this.swiper = Swiper;

			this.setupConfig();
			this.maybeSetupSwiper();
		} catch (error) {
			console.error('Failed to initialize Swiper:', error);
		}
	}

	setupConfig() {
		const raw = this.moduleEl.dataset.swiperConfig;

		if (!raw) {
			console.warn('No swiper config found', this.moduleEl);
		}

		try {
			this.config = raw.startsWith('{') ? JSON.parse(raw) : JSON.parse(atob(raw));
		} catch (e) {
			console.warn('Invalid Swiper config', e);
		}
	}

	maybeSetupSwiper() {
		if (this.swiperInstance) {
			this.swiperInstance.destroy();
			this.swiperInstance = null;
		}

		this.setupSwiper();
	}

	setupSwiper() {
		this.swiperInstance = new this.swiper(this.moduleEl, {
			...this.config,
			slidesPerView: 'auto',
			pagination: {
				el: '.swiper-pagination',
				type: this.usePaginationProgress ? 'progressbar' : 'bullets',
				clickable: true,
			},
			navigation: {
				nextEl: '.swiper-button-next',
				prevEl: '.swiper-button-prev',
			},
			slideClass: 'wp-block-qte-theme-child-slider-item',
		});

		// pagination element
		const pag = this.moduleEl.querySelector('.swiper-pagination');

		// clear on each new slide
		this.swiperInstance.on('slideChange', () => {
		  pag.querySelectorAll('.swiper-pagination-bullet').forEach((b) =>
			b.style.removeProperty('--percent')
		  );
		});
	  
		// drive gradient on the active bullet
		this.swiperInstance.on('autoplayTimeLeft', (s, time, progress) => {
			const fill = 1 - progress;
  			const pct  = `${Math.round(fill * 100)}%`;

			const active = pag.querySelector('.swiper-pagination-bullet-active');
			if (active) {
				active.style.setProperty('--percent', pct);
			}
		});

		if ( this.config.autoplay?.enabled && this.config.pauseButton ) {
			const btn    = this.moduleEl.querySelector( '.swiper-button-pause' );
			const iconEl = btn.querySelector( '.icon' );
			const playSVG  = btn.dataset.playSvg;
			const pauseSVG = btn.dataset.pauseSvg;

			if ( this.swiperInstance.autoplay.running ) {
				iconEl.innerHTML = pauseSVG;
				btn.setAttribute('aria-label', 'Pause slider');
				btn.classList.remove('is-paused');
			} else {
				iconEl.innerHTML = playSVG;
				btn.setAttribute('aria-label', 'Play slider');
				btn.classList.add('is-paused');
			}
		  
			// set initial class & label
			btn.classList.toggle( 'is-paused', ! this.swiperInstance.autoplay.running );
			btn.setAttribute(
			  'aria-label',
			  this.swiperInstance.autoplay.running ? 'Pause slider' : 'Play slider'
			);
		  
			btn.addEventListener( 'click', () => {
			  if ( this.swiperInstance.autoplay.running ) {
				this.swiperInstance.autoplay.stop();
				btn.classList.add( 'is-paused' );
				btn.setAttribute( 'aria-label', 'Play slider' );
				iconEl.innerHTML = playSVG;
			  } else {
				this.swiperInstance.autoplay.start();
				btn.classList.remove( 'is-paused' );
				btn.setAttribute( 'aria-label', 'Pause slider' );
				iconEl.innerHTML = pauseSVG;
			  }
			} );
		}
	}
}

// Wait for DOM to be interactive
if (document.readyState === 'interactive' || document.readyState === 'complete') {
	SliderSwipers.setupModules();
} else {
	document.addEventListener('DOMContentLoaded', SliderSwipers.setupModules);
}
